import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, MessageCircle, Calendar, ChevronRight, Truck, Package, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Layout from '@/components/layout/Layout';
import SEOHead from '@/components/seo/SEOHead';
import JsonLd from '@/components/seo/JsonLd';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import BookingModal from '@/components/BookingModal';

const serviceAreaSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Apple Device Repair Services in Bihar",
  "provider": {
    "@type": "LocalBusiness",
    "name": "iFix Apple Care",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Shop No. 6, Jassinandan Plaza, New Dak Bunglow Road",
      "addressLocality": "Patna",
      "addressRegion": "Bihar",
      "postalCode": "800001",
      "addressCountry": "IN"
    },
    "telephone": "+919798653591"
  },
  "areaServed": [
    { "@type": "State", "name": "Bihar", "containedInPlace": { "@type": "Country", "name": "India" } }
  ],
  "serviceType": ["iPhone Repair", "MacBook Repair", "iPad Repair", "Apple Watch Repair"]
};

const ServiceAreas = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const districts = [
    { name: 'Patna', description: 'Main service center location with same-day repairs', isHQ: true },
    { name: 'Gaya', description: 'Apple repair services for Gaya district residents' },
    { name: 'Muzaffarpur', description: 'iPhone and MacBook repair serving Muzaffarpur' },
    { name: 'Bhagalpur', description: 'Expert Apple device repairs for Bhagalpur area' },
    { name: 'Darbhanga', description: 'Professional Apple service for Darbhanga district' },
    { name: 'Purnia', description: 'iPhone screen and battery replacement near Purnia' },
    { name: 'Nalanda', description: 'Apple Watch and iPad repair serving Nalanda' },
    { name: 'Vaishali', description: 'Quick Apple repairs for Vaishali residents' },
    { name: 'Samastipur', description: 'MacBook service center for Samastipur area' },
    { name: 'Begusarai', description: 'iPhone repair services near Begusarai' },
    { name: 'Katihar', description: 'Apple device repair for Katihar district' },
    { name: 'Munger', description: 'Professional repairs serving Munger area' },
    { name: 'Chapra (Saran)', description: 'Apple service center for Chapra and Saran' },
    { name: 'Ara (Bhojpur)', description: 'iPhone MacBook repair near Ara Bhojpur' },
    { name: 'Sasaram (Rohtas)', description: 'Expert repairs for Sasaram Rohtas district' },
    { name: 'Hajipur', description: 'Same-day Apple repairs for Hajipur residents' },
    { name: 'Buxar', description: 'Apple Watch iPad repair near Buxar' },
    { name: 'Siwan', description: 'Professional iPhone service for Siwan area' },
    { name: 'Motihari (East Champaran)', description: 'Apple repairs for East Champaran' },
    { name: 'Bettiah (West Champaran)', description: 'MacBook repairs for West Champaran' },
    { name: 'Madhubani', description: 'iPhone screen replacement near Madhubani' },
    { name: 'Sitamarhi', description: 'Apple device service for Sitamarhi district' },
    { name: 'Sheohar', description: 'Expert Apple repairs serving Sheohar' },
    { name: 'Supaul', description: 'iPhone battery replacement near Supaul' },
    { name: 'Saharsa', description: 'Professional repairs for Saharsa area' },
    { name: 'Madhepura', description: 'Apple service center near Madhepura' },
    { name: 'Araria', description: 'iPhone MacBook repair for Araria district' },
    { name: 'Kishanganj', description: 'Apple Watch repair near Kishanganj' },
    { name: 'Gopalganj', description: 'Expert Apple repairs for Gopalganj' },
    { name: 'Jehanabad', description: 'Quick iPhone repairs near Jehanabad' },
    { name: 'Aurangabad', description: 'MacBook service for Aurangabad Bihar' },
    { name: 'Nawada', description: 'Apple device repair near Nawada' },
    { name: 'Jamui', description: 'Professional repairs for Jamui district' },
    { name: 'Lakhisarai', description: 'iPhone iPad repair near Lakhisarai' },
    { name: 'Sheikhpura', description: 'Apple service for Sheikhpura area' },
    { name: 'Khagaria', description: 'Expert repairs serving Khagaria' },
    { name: 'Arwal', description: 'Apple device service near Arwal' },
    { name: 'Banka', description: 'iPhone MacBook repair for Banka district' },
  ];

  const services = [
    { name: 'iPhone Repair', path: '/iphone-service', desc: 'Screen, battery, charging port repairs' },
    { name: 'MacBook Repair', path: '/macbook-service', desc: 'Screen, keyboard, logic board repairs' },
    { name: 'iPad Repair', path: '/ipad-service', desc: 'Screen, battery, charging repairs' },
    { name: 'Apple Watch Repair', path: '/watch-service', desc: 'Screen, battery, sensor repairs' },
  ];

  return (
    <Layout>
      <SEOHead
        title="Service Areas Bihar | Apple Repair Patna All Districts"
        description="Apple service center serving all 38 Bihar districts. iPhone MacBook iPad repair in Patna, Gaya, Muzaffarpur, Bhagalpur. Call +91 97986 53591"
        url="https://ifixappleservicecenter.in/service-areas"
      />
      <JsonLd data={serviceAreaSchema} />

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-br from-primary/5 via-background to-secondary overflow-hidden">
        <div className="container-apple relative z-10">
          <PageBreadcrumb
            items={[
              { name: 'Service Areas', path: '/service-areas' },
            ]}
          />

          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
              Apple Service Center Serving <span className="text-primary">All Bihar Districts</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl">
              Expert iPhone, MacBook, iPad, and Apple Watch repair services from our Patna center, 
              serving customers across all 38 districts of Bihar with genuine parts and warranty.
              <span className="block mt-2 text-primary font-medium">
                Courier pickup & drop available for all districts!
              </span>
            </p>

            <div className="flex flex-wrap gap-4">
              <Button variant="apple-blue" size="lg" onClick={() => setIsBookingOpen(true)}>
                <Calendar className="w-5 h-5" />
                Book Repair
              </Button>
              <Button variant="whatsapp" size="lg" asChild>
                <a
                  href="https://wa.me/919798653591?text=Hi%2C%20I%20need%20Apple%20device%20repair%20service"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp
                </a>
              </Button>
              <Button variant="call" size="lg" asChild>
                <a href="tel:9798653591">
                  <Phone className="w-5 h-5" />
                  Call Now
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services We Offer */}
      <section className="section-padding bg-secondary">
        <div className="container-apple">
          <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-8 text-center">
            Services Available Across Bihar
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => (
              <Link
                key={service.path}
                to={service.path}
                className="bg-background rounded-2xl p-6 shadow-apple hover:shadow-apple-lg transition-all hover:-translate-y-1"
              >
                <h3 className="font-semibold text-foreground mb-2">{service.name}</h3>
                <p className="text-sm text-muted-foreground">{service.desc}</p>
                <span className="inline-flex items-center text-primary text-sm mt-3">
                  Learn more <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Districts Grid */}
      <section className="section-padding">
        <div className="container-apple">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-4">
              All 38 Bihar Districts We Serve
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Whether you are in Patna or any other district in Bihar, our expert technicians are ready 
              to repair your Apple devices. Visit our Patna center or contact us for doorstep pickup options.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {districts.map((district) => (
              <div
                key={district.name}
                className={`rounded-xl p-4 border transition-all hover:shadow-apple ${
                  district.isHQ 
                    ? 'bg-primary/5 border-primary/20' 
                    : 'bg-background border-border hover:border-primary/30'
                }`}
              >
                <div className="flex items-start gap-3">
                  <MapPin className={`w-5 h-5 flex-shrink-0 mt-0.5 ${district.isHQ ? 'text-primary' : 'text-muted-foreground'}`} />
                  <div>
                    <h3 className="font-medium text-foreground">
                      {district.name}
                      {district.isHQ && (
                        <span className="ml-2 text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                          HQ
                        </span>
                      )}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">{district.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-secondary">
        <div className="container-apple">
          <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-8 text-center">
            How to Get Your Device Repaired
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                1
              </div>
              <h3 className="font-semibold text-foreground mb-2">Contact Us</h3>
              <p className="text-sm text-muted-foreground">
                Call, WhatsApp, or book online. Describe your device issue for a quick quote.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                2
              </div>
              <h3 className="font-semibold text-foreground mb-2">Visit or Courier Pickup</h3>
              <p className="text-sm text-muted-foreground">
                Visit our Patna center or opt for our courier pickup service. We will arrange pickup from your doorstep.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                3
              </div>
              <h3 className="font-semibold text-foreground mb-2">Repair & Deliver</h3>
              <p className="text-sm text-muted-foreground">
                We repair with genuine parts and courier it back to you. Fast, safe, and hassle-free!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Courier Pickup Service Section */}
      <section className="section-padding">
        <div className="container-apple">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Truck className="w-4 h-4" />
                Courier Pickup & Drop Service
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-4">
                Can't Visit Our Patna Center? No Problem!
              </h2>
              <p className="text-muted-foreground mb-6">
                We understand that not everyone can travel to Patna for repairs. That's why we offer 
                hassle-free courier pickup and drop services across all Bihar districts. Simply contact us, 
                and we will take care of everything.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-foreground">Free Pickup Arrangement</h4>
                    <p className="text-sm text-muted-foreground">We coordinate with courier partners to pick up your device from your location</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-foreground">Safe & Secure Packaging</h4>
                    <p className="text-sm text-muted-foreground">Your device is handled with care and proper packaging throughout</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-foreground">Quick Turnaround</h4>
                    <p className="text-sm text-muted-foreground">Same priority service as walk-in customers with fast delivery back</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-foreground">Real-time Updates</h4>
                    <p className="text-sm text-muted-foreground">Stay informed via WhatsApp about repair status and delivery</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <Button variant="whatsapp" asChild>
                  <a
                    href="https://wa.me/919798653591?text=Hi%2C%20I%20need%20courier%20pickup%20for%20Apple%20device%20repair"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Arrange Pickup via WhatsApp
                  </a>
                </Button>
                <Button variant="call" asChild>
                  <a href="tel:9798653591">
                    <Phone className="w-5 h-5" />
                    Call to Arrange
                  </a>
                </Button>
              </div>
            </div>

            <div className="bg-secondary rounded-3xl p-8">
              <h3 className="text-xl font-semibold text-foreground mb-6 text-center">
                How Courier Service Works
              </h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 font-bold">
                    1
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">Contact Us</h4>
                    <p className="text-sm text-muted-foreground">Call or WhatsApp with your device issue and location</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 font-bold">
                    2
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">We Arrange Pickup</h4>
                    <p className="text-sm text-muted-foreground">Courier picks up your device from your doorstep</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 font-bold">
                    3
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">Expert Repair</h4>
                    <p className="text-sm text-muted-foreground">Our technicians repair with genuine parts + warranty</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 font-bold">
                    4
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">Delivered Back</h4>
                    <p className="text-sm text-muted-foreground">Repaired device couriered safely back to you</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-foreground">
        <div className="container-apple text-center">
          <h2 className="text-2xl md:text-3xl font-semibold text-background mb-4">
            Need Apple Device Repair in Bihar?
          </h2>
          <p className="text-background/70 mb-6 max-w-xl mx-auto">
            No matter where you are in Bihar, we are here to help. Contact us today for expert 
            Apple device repair with genuine parts and warranty.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="apple-blue" size="lg" onClick={() => setIsBookingOpen(true)}>
              <Calendar className="w-5 h-5" />
              Book Appointment
            </Button>
            <Button variant="call" size="lg" asChild>
              <a href="tel:9798653591">
                <Phone className="w-5 h-5" />
                Call +91 97986 53591
              </a>
            </Button>
          </div>
        </div>
      </section>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </Layout>
  );
};

export default ServiceAreas;
